import {
	createFileRoute,
	Link,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { useEffect, useTransition } from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { Icon } from "@/components/icon";
import { Spinner } from "@/components/ui/spinner";
import { CreatePostButton } from "@/ui/create-post-button";
import { FeedOrderSelector } from "@/ui/feed-order-selector";
import { PostPreview } from "@/ui/post-preview";
import type {
	comunidade_feedQuery,
	FeedOrdering,
} from "./__generated__/comunidade_feedQuery.graphql";
import type { comunidadeFeedFragment$key } from "./__generated__/comunidadeFeedFragment.graphql";
import type { comunidadeFeedFragmentQuery } from "./__generated__/comunidadeFeedFragmentQuery.graphql";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute(
	"/_visibility/_topnav/_tabs/_sidebar/comunidade/",
)({
	component: RouteComponent,
	pendingComponent: () => {
		return (
			<div className="flex flex-col w-full gap-6">
				<CreatePostButton />
				<FeedOrderSelector />

				<div className="flex items-center justify-center py-12">
					<Spinner />
				</div>
			</div>
		);
	},
	pendingMinMs: 100,
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "__root__" });
	const { order } = useSearch({
		from: "/_visibility/_topnav/_tabs/_sidebar/comunidade",
	});

	const [isPending, startTransition] = useTransition();

	const [ref, entry] = useIntersectionObserver({
		threshold: 0,
		root: null,
		rootMargin: "0px",
	});

	const filter: FeedOrdering =
		order === "trending"
			? "TRENDING"
			: order === "oldest"
				? "OLDEST"
				: "NEWEST";

	const data = useLazyLoadQuery<comunidade_feedQuery>(
		graphql`
    query comunidade_feedQuery($slug: String!, $order: FeedOrdering!) {
      club(slug: $slug) {
        ...comunidadeFeedFragment @arguments(order: $order, club: $slug)

        scheduledContent {
          pageInfo {
            totalCount
          }
        }
      }
    }
  `,
		{ slug: club?.slug, order: filter },
	);

	const {
		data: feedData,
		loadNext,
		refetch,
	} = usePaginationFragment<
		comunidadeFeedFragmentQuery,
		comunidadeFeedFragment$key
	>(
		graphql`
	  fragment comunidadeFeedFragment on Club
		@refetchable(queryName: "comunidadeFeedFragmentQuery")
		@argumentDefinitions(
    	first: { type: "Int", defaultValue: 10 },
    	cursor: { type: "String" }
      order: { type: "FeedOrdering", defaultValue: NEWEST }
      club: { type: "String!" }
		)
		{
			feed(after: $cursor, first: $first, orderBy: $order, ignoreFixed: false)
			@connection(key: "comunidadeFeedFragment_feed")
		  {
				  __id
    			edges {
     			  node {
              ... on ContentInterface {
                ...on Post {
                  id
                  ...postPreviewFragment @arguments(club: $club)
                }
              }
          		__typename
       			}
    			}
          pageInfo {
            hasNextPage
          }
			}
		}
	`,
		data.club,
	);

	const { feed } = feedData;

	useEffect(() => {
		if (!isPending && entry?.isIntersecting && feed.pageInfo.hasNextPage) {
			startTransition(() => {
				loadNext(10);
			});
		}
	}, [entry?.isIntersecting, isPending, loadNext, feed.pageInfo.hasNextPage]);

	return (
		<div className="flex flex-col flex-1 w-full gap-6 xl:last:w-full xl:not-last:max-w-[calc(1000px-340px-1.5rem)] max-xl:w-full">
			<CreatePostButton />

			{(data.club.scheduledContent?.pageInfo?.totalCount || 0) > 0 ? (
				<Card className="p-6 flex flex-row items-center justify-between">
					<span className="text-sm font-medium">
						{data.club.scheduledContent?.pageInfo?.totalCount}{" "}
						{data.club.scheduledContent?.pageInfo?.totalCount === 1
							? "post agendado"
							: "posts agendados"}
					</span>

					<Link to="/posts-agendados">
						<Button variant="ghost">
							Visualizar
							<Icon name="MoveUpRight" />
						</Button>
					</Link>
				</Card>
			) : null}

			<FeedOrderSelector />

			{feed?.edges?.filter(Boolean).length ? (
				<>
					{feed?.edges.filter(Boolean).map(({ node }) => {
						if (node?.__typename === "Post") {
							return (
								<PostPreview
									key={node.id}
									post={node}
									showUniversalFeedPin
									onUpdate={() => {
										refetch({ order: filter }, { fetchPolicy: "network-only" });
									}}
								/>
							);
						}

						return null;
					})}

					<div ref={ref} className="size-1" />

					{isPending && (
						<div className="flex items-center justify-center">
							<Spinner />
						</div>
					)}
				</>
			) : (
				<div className="flex flex-col items-center justify-center gap-6">
					<Icon name="CircleSlash" className="size-16" />

					<span>Ainda não tem nada aqui :/</span>
				</div>
			)}
		</div>
	);
}
